import { FC, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { TravelBlockPassengerForm } from "Bands/components";
import {
  dataMetaSelector,
  dispatchGetTravelBlockPassengers,
  editTravelBlockPassenger,
} from "Bands/state";
import {
  BAND_ACTIONS,
  EventTravelLegType,
  ITravelBlockPassenger,
  ITravelBlockPassengerParams,
} from "Bands/types";
import { addTemporaryToast } from "shared/components/Toast";
import { IAPIState, IStatus, IFormButtons } from "shared/types";
import { useClearActionMetaOnUnMount } from "Bands/utils";
import { copy } from "shared/assets";
import { Modal } from "shared/components";
import { Guests } from "shared/assets/icons";
import { useBandContext } from "Bands/contexts";

const editCopy = copy.travelBlock.passenger.edit;

interface Props {
  blockId: string;
  travelLegs: EventTravelLegType[];
  handleClose: () => void;
  passenger: ITravelBlockPassenger;
}

export const EditTravelBlockPassengerModal: FC<Props> = ({
  blockId,
  travelLegs,
  handleClose,
  passenger,
}) => {
  const dispatch = useDispatch();
  const { bandId } = useBandContext();

  const { api: apiMeta } = useSelector(
    dataMetaSelector(BAND_ACTIONS.editTravelBlockPassenger)
  );

  useClearActionMetaOnUnMount(BAND_ACTIONS.editTravelBlockPassenger);

  useEffect(() => {
    if (apiMeta?.status === IAPIState.succeeded) {
      dispatchGetTravelBlockPassengers({
        dispatch,
        blockId,
      });

      dispatch(
        addTemporaryToast({
          status: IStatus.success,
          message: editCopy.messages.success,
        })
      );

      handleClose();
    }
  }, [apiMeta?.status, blockId, dispatch, handleClose]);

  const handleSubmit = (data: ITravelBlockPassengerParams) => {
    dispatch(editTravelBlockPassenger({ id: passenger.id, data }));
  };

  const buttons: IFormButtons = useMemo(
    () => ({
      primary: {
        "data-testid": "edit_travel_block_passenger",
      },
      secondary: {
        onClick: handleClose,
      },
    }),
    [handleClose]
  );

  return (
    <Modal icon={Guests} title={editCopy.title}>
      <TravelBlockPassengerForm
        disableUser
        bandId={bandId || ""}
        blockId={blockId}
        handleSubmit={handleSubmit}
        buttons={buttons}
        travelLegs={travelLegs}
        apiStatus={apiMeta?.status}
        apiError={apiMeta?.error}
        error={!!apiMeta?.error}
        passenger={passenger}
      />
    </Modal>
  );
};
